import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import FormInput from "../../components/CustomComponents/Input/formInput";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import CustomerSelector from "../../components/CustomComponents/modal/CustomerSelector";
import {
  getMarketingCustomerGuid,
  normalizeMarketingCustomer,
} from "../../components/CustomComponents/modal/customerSelectionModal/customerSelectionModal";
import {
  getThemeGradientButtonStyle,
  onThemeGradientButtonEnter,
  onThemeGradientButtonLeave,
} from "../../utils/themeGradientButtonStyle";
import "./marketingGroupFormPage.scss";

const getApiPayload = (response) =>
  response?.data?.data ?? response?.data?.Data ?? null;

const parseMarketingGroupDetail = (payload) => {
  if (!payload || typeof payload !== "object") {
    return { group: null, customerGuids: [], customers: [] };
  }

  const nestedGroup = payload.group ?? payload.Group;
  const hasGroupFields =
    nestedGroup != null ||
    payload.marketingGroup_ID != null ||
    payload.MarketingGroup_ID != null ||
    payload.groupTitle != null ||
    payload.GroupTitle != null;

  const group = nestedGroup ?? (hasGroupFields ? payload : null);
  const rawGuids = payload.customerGuids ?? payload.CustomerGuids ?? [];
  const customerGuids = Array.isArray(rawGuids)
    ? rawGuids.map((guid) => String(guid).trim()).filter(Boolean)
    : [];
  const rawCustomers = payload.customers ?? payload.Customers ?? [];
  const customers = Array.isArray(rawCustomers) ? rawCustomers : [];

  return { group, customerGuids, customers };
};

const MarketingGroupFormPage = ({ mode }) => {
  const isEdit = mode === "edit";
  const { groupId } = useParams();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const marketingGroupId = isEdit ? Number(groupId) || 0 : 0;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [groupTitle, setGroupTitle] = useState("");
  const [description, setDescription] = useState("");
  const [marketingGroupGuid, setMarketingGroupGuid] = useState(() =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : ""
  );
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  const loadCustomersForGroup = async (customerGuidsFromDetail = []) => {
    try {
      const customersResponse = await axiosInstance.get(
        APIs.GET_MARKETING_GROUP_CUSTOMERS(marketingGroupId),
        { params: { CompID } }
      );
      const customerRows = getApiPayload(customersResponse);
      if (Array.isArray(customerRows) && customerRows.length > 0) {
        return customerRows.map(normalizeMarketingCustomer);
      }
    } catch {
      // Fall through to picker-based load (endpoint may not be deployed yet)
    }

    try {
      const pickerResponse = await axiosInstance.get(
        APIs.GET_CUSTOMERS_FOR_MARKETING_GROUP_PICKER,
        {
          params: {
            CompID,
            MarketingGroup_ID: marketingGroupId,
            Search: "",
            SortBy: "Cust_Fname",
            SortOrder: "ASC",
            PageNumber: 1,
            PageSize: 5000,
          },
        }
      );
      const rows = getApiPayload(pickerResponse);
      if (!Array.isArray(rows) || rows.length === 0) return [];

      const guidSet = new Set(
        (Array.isArray(customerGuidsFromDetail) ? customerGuidsFromDetail : [])
          .map((guid) => String(guid).trim().toLowerCase())
          .filter(Boolean)
      );

      const fromPicker = rows
        .filter((row) => {
          if (row.isSelected || row.IsSelected) return true;
          const guid = getMarketingCustomerGuid(row).toLowerCase();
          return guidSet.size > 0 && guid && guidSet.has(guid);
        })
        .map(normalizeMarketingCustomer);

      if (fromPicker.length > 0) return fromPicker;

      if (guidSet.size === 0) return [];

      return rows
        .filter((row) => {
          const guid = getMarketingCustomerGuid(row).toLowerCase();
          return guid && guidSet.has(guid);
        })
        .map(normalizeMarketingCustomer);
    } catch {
      return [];
    }
  };

  useEffect(() => {
    if (!isEdit) {
      setLoading(false);
      return;
    }

    if (!marketingGroupId || !CompID) {
      return;
    }

    const loadGroup = async () => {
      try {
        setLoading(true);
        const groupResponse = await axiosInstance.get(
          APIs.GET_MARKETING_GROUP_BY_ID(marketingGroupId),
          { params: { CompID } }
        );

        if (!groupResponse.data?.status) {
          showErrorToast(
            groupResponse.data?.message || "Marketing group not found."
          );
          navigate("/marketing/manageMarketingGroups");
          return;
        }

        const { group, customerGuids, customers: customersFromDetail } =
          parseMarketingGroupDetail(getApiPayload(groupResponse));

        if (!group) {
          showErrorToast("Marketing group not found.");
          navigate("/marketing/manageMarketingGroups");
          return;
        }

        setGroupTitle(group.groupTitle ?? group.GroupTitle ?? "");
        setDescription(
          group.description ?? group.Description ?? ""
        );
        const loadedGuid =
          group.marketingGroup_Guid ?? group.MarketingGroup_Guid ?? "";
        if (loadedGuid) {
          setMarketingGroupGuid(loadedGuid);
        }

        let customers = (customersFromDetail ?? []).map(normalizeMarketingCustomer);
        if (customers.length === 0) {
          customers = await loadCustomersForGroup(customerGuids);
        }
        setSelectedCustomers(customers);

        if (customerGuids.length > 0 && customers.length === 0) {
          showErrorToast(
            "Group loaded but customer list could not be loaded. Re-select customers and save."
          );
        }
      } catch {
        showErrorToast("Failed to load marketing group.");
        navigate("/marketing/manageMarketingGroups");
      } finally {
        setLoading(false);
      }
    };

    loadGroup();
  }, [isEdit, marketingGroupId, CompID, navigate]);

  const handleSave = async () => {
    if (!groupTitle.trim()) {
      showErrorToast("Please provide a group title.");
      return;
    }

    setSaving(true);
    try {
      const customerGuids = selectedCustomers
        .map((c) => getMarketingCustomerGuid(c))
        .filter((guid) => guid);

      const payload = {
        marketingGroup_ID: isEdit ? marketingGroupId : 0,
        compID: CompID,
        groupTitle: groupTitle.trim(),
        description: description.trim() || null,
        customerGuids,
        CustomerGuids: customerGuids,
      };
      if (marketingGroupGuid) {
        payload.marketingGroup_Guid = marketingGroupGuid;
      }

      const response = await axiosInstance.post(
        APIs.POST_ADD_UPDATE_MARKETING_GROUP,
        payload
      );

      if (response.data?.status) {
        showSuccessToast(
          response.data.message ||
            (isEdit
              ? "Marketing group updated successfully."
              : "Marketing group created successfully.")
        );
        navigate("/marketing/manageMarketingGroups");
      } else {
        showErrorToast(
          response.data?.message || "Could not save marketing group."
        );
      }
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || "Error saving marketing group."
      );
    } finally {
      setSaving(false);
    }
  };

  const gradientBtnStyle = getThemeGradientButtonStyle(themeColor);
  const pageTitle = isEdit ? "Edit Marketing Group" : "Create Marketing Group";
  const breadcrumbActive = isEdit ? "Edit Marketing Group" : "Add Marketing Group";
  const customerCount = selectedCustomers.length;

  return (
    <div className={`mkg-form${isDarkMode ? " mkg-form--dark" : ""}`}>
      <div className="mkg-form__header">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/marketing/manageMarketingGroups">Marketing Groups</Link>
            </li>
            <li className="breadcrumb-item active">{breadcrumbActive}</li>
          </ol>
          <h1 className="page-header mb-0">{pageTitle}</h1>
        </div>

        <div className="mkg-form__actions">
          <button
            type="button"
            className="btn mkg-form__btn-cancel"
            onClick={() => navigate("/marketing/manageMarketingGroups")}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="btn mkg-form__btn-save"
            style={gradientBtnStyle}
            onMouseEnter={(e) => onThemeGradientButtonEnter(e, themeColor)}
            onMouseLeave={(e) => onThemeGradientButtonLeave(e, themeColor)}
          >
            <i className={`fa ${saving ? "fa-spinner fa-spin" : "fa-save"} me-2`} />
            {saving ? "Saving..." : isEdit ? "Save changes" : "Create group"}
          </button>
        </div>
      </div>

      <GridSectionLoader loading={loading} minHeight="280px">
        <div className="mkg-form__grid">
          <section className="mkg-form__card">
            <div className="mkg-form__card-head">
              <div>
                <h2 className="mkg-form__card-title">
                  <i className="fa fa-info-circle" />
                  General
                </h2>
                <p className="mkg-form__card-sub">
                  Name this group and add an optional description.
                </p>
              </div>
            </div>
            <div className="mkg-form__card-body">
              <div className="mkg-form__field">
                <FormInput
                  label="Group title"
                  name="groupTitle"
                  value={groupTitle}
                  onchange={(e) => setGroupTitle(e.target.value)}
                  placeHolder="e.g. Summer VIP customers"
                />
              </div>
              <div className="mkg-form__field">
                <label
                  className="mkg-form__label"
                  style={{ color: isDarkMode ? "#ffffff" : "#000000" }}
                >
                  Description
                </label>
                <textarea
                  className="mkg-form__textarea"
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Short description of this marketing group"
                />
                <div className="mkg-form__hint">Optional — helps your team recognise this audience later.</div>
              </div>
            </div>
          </section>

          <section className="mkg-form__card">
            <div className="mkg-form__card-head">
              <div>
                <h2 className="mkg-form__card-title">
                  <i className="fa fa-users" />
                  Customers
                  {customerCount > 0 && (
                    <span className="badge bg-primary ms-1">{customerCount}</span>
                  )}
                </h2>
                <p className="mkg-form__card-sub">
                  Choose who belongs in this marketing audience.
                </p>
              </div>
            </div>
            <div className="mkg-form__card-body">
              <CustomerSelector
                selectedCustomers={selectedCustomers}
                setSelectedCustomers={setSelectedCustomers}
                showModal={showCustomerModal}
                setShowModal={setShowCustomerModal}
                marketingGroupId={marketingGroupId}
              />
            </div>
          </section>
        </div>
      </GridSectionLoader>
    </div>
  );
};

export default MarketingGroupFormPage;
